import { useState } from 'react'
import { DashboardLayout } from '../../components/layout'
import { Button, Card } from '../../components/ui'
import { Input } from '../../components/forms'
import { useAuth } from '../../context/AuthContext'
import { useToast } from '../../context/ToastContext'
import { getSupabase } from '../../lib/supabase'

function ProfilePage() {
  const { user, profile, loadProfile } = useAuth()
  const toast = useToast()

  const [name, setName] = useState(profile?.name || '')
  const [nameLoading, setNameLoading] = useState(false)

  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordLoading, setPasswordLoading] = useState(false)

  const handleUpdateName = async (e) => {
    e.preventDefault()
    if (!name.trim()) {
      toast.error('Name is required')
      return
    }

    setNameLoading(true)
    const client = getSupabase()

    try {
      const { error } = await client
        .from('profiles')
        .update({ name: name.trim() })
        .eq('id', user.id)

      if (error) throw error

      await loadProfile(user.id)
      toast.success('Name updated successfully')
    } catch (err) {
      toast.error('Failed to update name')
    }

    setNameLoading(false)
  }

  const handleChangePassword = async (e) => {
    e.preventDefault()

    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters')
      return
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match')
      return
    }

    setPasswordLoading(true)
    const client = getSupabase()

    try {
      const { error } = await client.auth.updateUser({
        password: newPassword
      })

      if (error) throw error

      toast.success('Password updated successfully')
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    } catch (err) {
      toast.error(err.message || 'Failed to update password')
    }

    setPasswordLoading(false)
  }

  return (
    <DashboardLayout title="Profile">
      {/* Profile Info */}
      <Card className="mb-6">
        <h3 className="card-title mb-4">Profile Information</h3>

        <div className="mb-4 p-4 rounded" style={{ background: 'var(--gray-50)' }}>
          <div className="flex items-center gap-4">
            <div
              style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                background: 'var(--primary)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.5rem',
                fontWeight: 'bold',
              }}
            >
              {(profile?.name || user?.email || '?').substring(0, 2).toUpperCase()}
            </div>
            <div>
              <p className="font-medium text-lg">{profile?.name || 'No name set'}</p>
              <p className="text-muted">{user?.email}</p>
              <p className="text-sm text-muted capitalize">Role: {profile?.role || 'member'}</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleUpdateName}>
          <Input
            label="Display Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
          />
          <Button type="submit" loading={nameLoading}>
            Update Name
          </Button>
        </form>
      </Card>

      {/* Change Password */}
      <Card className="mb-6">
        <h3 className="card-title mb-4">Change Password</h3>
        <p className="text-muted text-sm mb-4">
          Password must be at least 8 characters long.
        </p>

        <form onSubmit={handleChangePassword}>
          <Input
            label="New Password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Enter new password"
            autoComplete="new-password"
          />

          <Input
            label="Confirm New Password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm new password"
            autoComplete="new-password"
          />

          <Button type="submit" loading={passwordLoading}>
            Change Password
          </Button>
        </form>
      </Card>

      {/* Account Info */}
      <Card>
        <h3 className="card-title mb-4">Account Details</h3>
        <div className="flex flex-col gap-2">
          <div className="flex justify-between py-2 border-b" style={{ borderColor: 'var(--gray-100)' }}>
            <span className="text-muted">Email</span>
            <span>{user?.email}</span>
          </div>
          <div className="flex justify-between py-2 border-b" style={{ borderColor: 'var(--gray-100)' }}>
            <span className="text-muted">User ID</span>
            <span className="text-sm font-mono">{user?.id}</span>
          </div>
          <div className="flex justify-between py-2 border-b" style={{ borderColor: 'var(--gray-100)' }}>
            <span className="text-muted">Account Created</span>
            <span>{user?.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}</span>
          </div>
          <div className="flex justify-between py-2">
            <span className="text-muted">Last Sign In</span>
            <span>{user?.last_sign_in_at ? new Date(user.last_sign_in_at).toLocaleDateString() : '-'}</span>
          </div>
        </div>
      </Card>
    </DashboardLayout>
  )
}

export default ProfilePage
